// src/components/ScoringForm.js
// Updated: 11/02/2025 07:05 PM EST
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

// Helper function to initialize scores state
const initializeScores = (session) => {
    const initialScores = {};
    if (session?.categories) {
        session.categories.forEach((cat, catIndex) => {
            if (session.useSimplifiedRubric) {
                 initialScores[`${catIndex}`] = ''; 
            } else {
                 cat.criteria.forEach((crit, critIndex) => {
                     initialScores[`${catIndex}_${critIndex}`] = ''; 
                 });
            }
        });
    }
    return initialScores;
};

// Helper function to initialize remarks state
const initializeRemarks = (session) => {
    const initialRemarks = {};
    if (session?.categories) {
        session.categories.forEach((cat, catIndex) => {
            initialRemarks[`cat_remark_${catIndex}`] = '';
        });
    }
    return initialRemarks;
};


export default function ScoringForm({ session, packageData, existingScore }) {
    const { currentUser } = useAuth();
    const [scores, setScores] = useState(() => initializeScores(session));
    const [categoryRemarks, setCategoryRemarks] = useState(() => initializeRemarks(session)); // NEW: State for category remarks
    const [generalComments, setGeneralComments] = useState(''); // Renamed original 'comments'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Initialize form state when component mounts or existingScore changes
    useEffect(() => {
        if (existingScore?.scores) {
            setScores(prevScores => ({ ...prevScores, ...existingScore.scores }));
            setCategoryRemarks(existingScore.categoryRemarks || initializeRemarks(session)); // NEW: Load or initialize remarks
            setGeneralComments(existingScore.generalComments || ''); // Load general comments
        } else {
            setScores(initializeScores(session));
            setCategoryRemarks(initializeRemarks(session)); // Initialize empty remarks
            setGeneralComments('');
        }
        setError('');
        setSuccess('');
    }, [existingScore, session, packageData.id]);


    const handleScoreChange = (scoreKey, value, maxPoints) => {
        const numericValue = value === '' ? '' : Number(value);
        
        if (value !== '' && (isNaN(numericValue) || numericValue < 0 || numericValue > maxPoints)) {
             console.warn(`Invalid score value: ${value} for maxPoints: ${maxPoints}`);
             return; 
        }
        setScores({
            ...scores,
            [scoreKey]: numericValue,
        });
        setError(''); setSuccess('');
    };
    
    // NEW: Handler for category remarks
    const handleRemarkChange = (categoryIndex, value) => {
        setCategoryRemarks({
            ...categoryRemarks,
            [`cat_remark_${categoryIndex}`]: value,
        });
        setError(''); setSuccess('');
    };


    const calculateTotals = () => {
        let totalRawScore = 0, totalWeightedScore = 0, totalPossibleRaw = 0, totalPossibleWeighted = 0;
        
        session.categories.forEach((cat, catIndex) => {
            const isSimplified = session.useSimplifiedRubric;
            const scoreKey = isSimplified ? `${catIndex}` : `${catIndex}_0`; 
            
            if (isSimplified) {
                const scoreValue = Number(scores[scoreKey] || 0); 
                totalRawScore += scoreValue;
                totalWeightedScore += scoreValue * (cat.weight || 1);
                totalPossibleRaw += cat.maxPoints;
                totalPossibleWeighted += cat.maxPoints * (cat.weight || 1);

            } else {
                cat.criteria.forEach((crit, critIndex) => {
                    const detailedScoreKey = `${catIndex}_${critIndex}`;
                    const scoreValue = Number(scores[detailedScoreKey] || 0);
                    totalRawScore += scoreValue;
                    totalWeightedScore += scoreValue * crit.weight;
                    totalPossibleRaw += crit.maxPoints;
                    totalPossibleWeighted += crit.maxPoints * crit.weight;
                });
            }
        });

        return { totalRawScore, totalWeightedScore, totalPossibleRaw, totalPossibleWeighted };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess(''); setIsSubmitting(true);

        // Validation: Check if all scores are entered
        let allScoresEntered = true;
        session.categories.forEach((cat, catIndex) => {
             const isSimplified = session.useSimplifiedRubric;

             if (isSimplified) {
                 const scoreValue = scores[`${catIndex}`];
                 if (scoreValue === '' || isNaN(Number(scoreValue)) || Number(scoreValue) < 0 || Number(scoreValue) > cat.maxPoints) {
                    allScoresEntered = false;
                 }
             } else {
                 cat.criteria.forEach((crit, critIndex) => {
                     const scoreValue = scores[`${catIndex}_${critIndex}`];
                     if (scoreValue === '' || isNaN(Number(scoreValue)) || Number(scoreValue) < 0 || Number(scoreValue) > crit.maxPoints) {
                        allScoresEntered = false;
                     }
                 });
             }
        });

        if (!allScoresEntered) {
            setError("Please enter a valid score (0 to Max Points) for all criteria/categories.");
            setIsSubmitting(false);
            return;
        }

        const { totalRawScore, totalWeightedScore } = calculateTotals();
        
        // --- NEW: Check for impersonation ID from session storage ---
        const impersonatingAdminId = sessionStorage.getItem('impersonating_admin_id');

        const scoreData = {
            sessionId: session.id, packageId: packageData.id, packageName: packageData.name,
            reviewerId: currentUser.uid, reviewerName: currentUser.displayName || 'User',
            scores: scores,
            categoryRemarks: categoryRemarks, // NEW: Include category remarks
            generalComments: generalComments, // Include general comments
            totalRawScore, totalWeightedScore,
            submittedAt: serverTimestamp(), orgId: session.orgId, 
            // --- NEW: Conditionally add the impersonation field ---
            ...(impersonatingAdminId && { impersonatedBy: impersonatingAdminId }),
        };

        try {
            const scoreRef = doc(db, 'scoringSessions', session.id, 'scores', `${packageData.id}_${currentUser.uid}`);
            await setDoc(scoreRef, scoreData, { merge: true });
            setSuccess('Score submitted successfully!');
        } catch (err) { console.error("Error submitting score:", err); setError("Failed to submit score."); }
        finally { setIsSubmitting(false); }
    };

    const { totalRawScore, totalWeightedScore, totalPossibleRaw, totalPossibleWeighted } = calculateTotals();
    const isAlreadySubmitted = !!existingScore; 

    return (
        <form onSubmit={handleSubmit} className="rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 border-b pb-2 text-2xl font-semibold text-gray-800">
                Scoring: <span className="text-blue-700">{packageData.name}</span>
            </h2>
            {packageData.description && <p className="mb-4 text-sm text-gray-600">{packageData.description}</p>}

            {session.categories.map((category, catIndex) => {
                const isSimplified = session.useSimplifiedRubric;
                const scoreKey = isSimplified ? `${catIndex}` : `${catIndex}_0`;
                const remarksKey = `cat_remark_${catIndex}`;

                return (
                    <div key={catIndex} className="mb-6 rounded-md border border-gray-200 p-4">
                        <h3 className="mb-3 text-lg font-semibold text-gray-700">{category.name}</h3>

                        {/* Display Detailed Criteria Description if Simplified Mode */}
                        {isSimplified && category.simplifiedCriteria && (
                            <div className="mb-3 rounded-md bg-blue-50 p-3 text-sm text-gray-700">
                                <p className="font-medium">Criteria Breakdown:</p>
                                <p className="whitespace-pre-wrap">{category.simplifiedCriteria}</p>
                            </div>
                        )}

                        {/* Score Input Fields */}
                        <div className="space-y-3">
                            {isSimplified ? (
                                // --- Simplified Input (One per Category) ---
                                <div className="grid grid-cols-3 items-center gap-4">
                                    <label htmlFor={scoreKey} className="col-span-2 text-sm font-medium text-gray-600">
                                        Score (0 to {category.maxPoints})
                                        {category.weight > 1 && <span className="ml-1 text-xs text-gray-400">(W: {category.weight})</span>}
                                    </label>
                                    <input
                                        type="number" id={scoreKey} name={scoreKey} min="0" 
                                        max={category.maxPoints} step="1"
                                        value={scores[scoreKey] ?? ''}
                                        onChange={(e) => handleScoreChange(scoreKey, e.target.value, category.maxPoints)}
                                        required
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                                        disabled={isAlreadySubmitted}
                                    />
                                </div>
                            ) : (
                                // --- Detailed Inputs (Multiple per Category) ---
                                category.criteria.map((criterion, critIndex) => {
                                    const detailedScoreKey = `${catIndex}_${critIndex}`;
                                    return (
                                        <div key={critIndex} className="grid grid-cols-3 items-center gap-4">
                                            <label htmlFor={detailedScoreKey} className="col-span-2 text-sm font-medium text-gray-600">
                                                {criterion.name}
                                                <span className="ml-1 text-xs text-gray-400">(Max: {criterion.maxPoints}, W: {criterion.weight})</span>
                                            </label>
                                            <input
                                                type="number" id={detailedScoreKey} name={detailedScoreKey} min="0" 
                                                max={criterion.maxPoints} step="1"
                                                value={scores[detailedScoreKey] ?? ''}
                                                onChange={(e) => handleScoreChange(detailedScoreKey, e.target.value, criterion.maxPoints)}
                                                required
                                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                                                disabled={isAlreadySubmitted}
                                            />
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* NEW: Remarks Textarea for the Category */}
                        <div className="mt-4 pt-3 border-t border-gray-100">
                           <label htmlFor={remarksKey} className="mb-1 block text-sm font-medium text-gray-700">
                             Remarks for {category.name} (Optional)
                           </label>
                           <textarea
                             id={remarksKey} rows="2"
                             value={categoryRemarks[remarksKey]}
                             onChange={(e) => handleRemarkChange(catIndex, e.target.value)}
                             className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                             disabled={isAlreadySubmitted}
                           />
                        </div>
                    </div>
                );
            })}

            {/* Totals Display */}
            <div className="my-4 rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm">
                <div className="flex justify-between"> <span>Raw Score:</span> <span className="font-semibold">{totalRawScore} / {totalPossibleRaw}</span> </div>
                <div className="flex justify-between"> <span>Weighted Score:</span> <span className="font-semibold">{totalWeightedScore.toFixed(1)} / {totalPossibleWeighted.toFixed(1)}</span> </div>
            </div>

            {/* General Comments Section (Renamed from just 'Comments') */}
            <div className="mt-6">
                <label htmlFor="general-comments" className="mb-1 block text-sm font-medium text-gray-700"> General Session Comments (Overall Feedback) </label>
                <textarea
                    id="general-comments" rows="4" 
                    value={generalComments} 
                    onChange={(e) => setGeneralComments(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                    disabled={isAlreadySubmitted}
                />
            </div>

            {error && <p className="mt-4 text-sm font-medium text-red-600">{error}</p>}
            {success && <p className="mt-4 text-sm font-medium text-green-600">{success}</p>}

            {/* Submit Button */}
            <div className="mt-6 flex justify-end">
                <button type="submit" disabled={isSubmitting || isAlreadySubmitted} className="rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" >
                    {isSubmitting ? 'Submitting...' : isAlreadySubmitted ? 'Submitted ✓' : 'Submit Score'}
                </button>
            </div>
        </form>
    );
}